import React, { useState, useEffect } from "react";
import { Mail, AlertCircle } from "lucide-react";
import { AuthContainer } from "./AuthContainer";
import { useAuth } from "./AuthContext";
import { useApp } from "./AppContext";
import { apiPost } from "../services/api.service";
import { useTranslation } from "../i18n/TranslationContext";

interface EmailCheckResponse {
	status: "not_found" | "unverified" | "verified";
	message: string;
	email?: string;
	canLogin?: boolean;
	requiresVerification?: boolean;
	extraId?: number;
}

export function SignIn() {
	const {
		setCurrentStep,
		checkUserExists,
		updateAuthData,
		handleGoogleAuth,
		authData,
	} = useAuth();
	const {
		setIsAuthenticated,
		setUserProfile,
		initializeExistingUser,
		updateOnboardingState,
	} = useApp();
	const { t } = useTranslation();

	// Initialize email from authData if available (e.g., from expired magic link)
	const [email, setEmail] = useState(authData.email || "");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string>("");

	useEffect(() => {
		// If authData has email, use it (don't reset to empty)
		if (authData.email) {
			setEmail(authData.email);
		}
	}, [authData.email]);

	const validateEmail = () => {
		if (!email) {
			setError(t("auth.validation.emailRequired"));
			return false;
		} else if (!/\S+@\S+\.\S+/.test(email)) {
			setError(t("auth.validation.emailInvalid"));
			return false;
		}
		setError("");
		return true;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!validateEmail()) return;

		setIsLoading(true);
		setError("");

		try {
			// Check email status with backend
			const response = await apiPost<EmailCheckResponse>(
				"/auth/check-email",
				{ email },
				{ notification: { enabled: false } }
			);

			// Store email and extraId in auth context
			updateAuthData({
				email,
				extraId: response?.data?.extraId,
			});

			switch (response?.data?.status) {
				case "verified":
					// User exists and is verified - go to password screen
					// For now, we'll need to create a PasswordScreen component
					// Store that we need password entry
					sessionStorage.setItem(
						"pendingAuth",
						JSON.stringify({
							email,
							needsPassword: true,
							extraId: response?.data?.extraId,
						})
					);
					setCurrentStep(6); // Assuming step 6 is password screen
					break;

				case "unverified":
					// User exists but needs verification - go to email verification screen
					// extraId already stored in auth context above
					setCurrentStep(1); // Step 1 is EmailVerification in AuthFlow
					break;

				case "not_found":
					// External user or user doesn't exist - show same message
					setError(t("auth.signIn.noAccount"));
					break;

				default:
					// Unexpected status
					setError(t("auth.errors.unableToCheck"));
					break;
			}
		} catch (err: any) {
			console.error("Email check error:", err);
			// This is a real error (network, server down, etc.)
			setError(t("auth.errors.networkError"));
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<AuthContainer>
			<div className="text-center mb-8">
				<h1 className="text-[28px] font-bold text-ucm-text-dark mb-8 leading-tight">
					{t("auth.signIn.title")}
				</h1>
			</div>

			<form onSubmit={handleSubmit} className="space-y-6">
				<div>
					<label className="block text-sm font-medium text-ucm-text-medium mb-2">
						{t("auth.signIn.emailLabel")}
					</label>
					<div className="relative">
						<input
							type="email"
							value={email}
							onChange={(e) => {
								setEmail(e.target.value);
								setError(""); // Clear error on typing
							}}
							className={`w-full h-14 px-4 pr-12 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ucm-primary-teal focus:border-transparent ${
								error ? "border-ucm-error-red" : "border-ucm-border-light"
							}`}
							placeholder={t("auth.signIn.emailPlaceholder")}
							disabled={isLoading}
						/>
						<Mail className="absolute right-4 top-1/2 transform -translate-y-1/2 text-ucm-text-light w-5 h-5" />
					</div>
					{error && (
						<div className="mt-2 flex items-start">
							<AlertCircle className="w-4 h-4 text-ucm-error-red mr-2 mt-0.5 flex-shrink-0" />
							<p className="text-sm text-ucm-error-red">{error}</p>
						</div>
					)}
				</div>

				<button
					type="submit"
					disabled={isLoading}
					className={`w-full h-14 bg-ucm-primary-teal text-white font-semibold rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ucm-primary-teal focus:ring-offset-2 text-[16px] ${
						isLoading
							? "opacity-50 cursor-not-allowed"
							: "hover:bg-ucm-primary-teal-hover"
					}`}
				>
					{isLoading ? (
						<div className="flex items-center justify-center">
							<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
							{t("auth.signIn.checking")}
						</div>
					) : (
						t("common.continue")
					)}
				</button>
			</form>
		</AuthContainer>
	);
}
